// OCR Service Client
// Uses Paathshaala API as proxy to HuggingFace OCR (avoids CORS, keeps token server-side)
// Falls back to direct HF Space call if API proxy fails

const API_URL = import.meta.env.VITE_API_URL || '';
const HF_TOKEN = import.meta.env.VITE_HF_TOKEN || '';
const OCR_BASE_URL = 'https://hellosguhs-ocr-automation-olmocr.hf.space';

export interface OCROptions {
  provider?: 'ollama' | 'huggingface' | 'mock';
}

export interface OCRResult {
  success: boolean;
  text?: string;
  jobId?: string;
  error?: string;
}

async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> || {}),
  };
  // Only add HF token for direct HF calls, not API proxy
  if (url.includes('huggingface.co') || url.includes('.hf.space')) {
    if (HF_TOKEN) {
      headers['Authorization'] = `Bearer ${HF_TOKEN}`;
    }
  }
  return fetch(url, { ...options, headers });
}

export const ocrService = {
  // Primary: Upload via Paathshaala API proxy
  async uploadFile(file: File, options?: OCROptions): Promise<OCRResult> {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.provider) {
      formData.append('provider', options.provider);
    }

    // Try API proxy first (avoids CORS, server has HF token)
    if (API_URL) {
      try {
        const response = await fetch(`${API_URL}/api/ocr/upload`, {
          method: 'POST',
          body: formData,
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            return {
              success: true,
              text: data.data.text || data.data.result?.text,
              jobId: data.data.jobId,
            };
          }
        }
      } catch {
        // API proxy failed, try direct
      }
    }

    // Fallback: Direct to HF Space
    return this.uploadDirect(file, options);
  },

  // Fallback: Direct upload to HF Space
  async uploadDirect(file: File, options?: OCROptions): Promise<OCRResult> {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.provider) {
      formData.append('provider', options.provider);
    }

    try {
      const response = await fetchWithAuth(`${OCR_BASE_URL}/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        // Check if it's HTML (HF Space sleeping/private)
        if (errorText.includes('<!DOCTYPE') || errorText.includes('<html')) {
          return {
            success: false,
            error: 'OCR service is currently unavailable (Space may be sleeping or private). Please try again later or contact admin.',
          };
        }
        return { success: false, error: `OCR Error ${response.status}: ${errorText.slice(1,100)}` };
      }

      const data = await response.json();
      
      // HF Space returns job_id, we need to poll for results
      if (data.job_id) {
        return await this.pollForResult(data.job_id);
      }
      
      return {
        success: true,
        text: data.text || data.result?.text || JSON.stringify(data),
      };
    } catch (err: any) {
      return {
        success: false,
        error: err.message || 'Network error. OCR service may be unavailable.',
      };
    }
  },

  // Poll HF Space for job result
  // The status endpoint combines status + result; when completed, result is nested
  async pollForResult(jobId: string, maxAttempts = 30): Promise<OCRResult> {
    const pollUrl = `${OCR_BASE_URL}/status/${jobId}`;
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const response = await fetchWithAuth(pollUrl);
        if (!response.ok) {
          await new Promise(r => setTimeout(r, 2000));
          continue;
        }
        
        const data = await response.json();
        
        if (data.status === 'completed') {
          // Result is nested under `result` key
          const result = data.result || data;
          const text = result.combined_text && result.combined_text !== 'None'
            ? result.combined_text
            : result.sanitized_text && result.sanitized_text !== 'None'
            ? result.sanitized_text
            : result.pages
              ? result.pages.map((p: any) => p.content).filter(Boolean).join('\n')
              : null;
          return { success: true, text: text || '(no text extracted)', jobId };
        }
        
        if (data.status === 'failed') {
          return { success: false, error: data.error || data.result?.error || 'OCR processing failed' };
        }
        
        // Still processing, wait 2 seconds
        await new Promise(r => setTimeout(r, 2000));
      } catch {
        await new Promise(r => setTimeout(r, 2000));
      }
    }
    
    return { success: false, error: 'OCR timed out. The document may be too large or the service is busy.' };
  },
};
